import { apiClient } from '../utils/api-client';
import { downloadBlobAsFile } from './documentService';
import type {
  InvestorExportLogList,
  InvestorExportLogParams,
} from '@/types/investorExport';
import type {
  InvestorEmailLogDetail,
  InvestorEmailLogList,
  InvestorEmailLogParams,
} from '../types/investorEmail';
import { formatCurrency } from '../utils/formatters';
import { toNumberRecord } from '../utils/number-operations';
import { mapInvestorInvestmentOverview } from './investorOverviewMapper';
import {
  mapInvestorDetail,
  mapFundInvestorStatistics,
  mapInvestorFundTransferResponse,
  mapPaginatedInvestorSummary,
} from './investorMapper';
import type {
  InvestorDetail,
  InvestorCreateData,
  InvestorUpdateData,
  InvestorUpdateResponse,
  FundRelationshipDeleteResponse,
  InvestorDeleteResponse,
  InvestorContact,
  InvestorContactCreateData,
  InvestorContactSearchResult,
  InvestorContactUpdateData,
  InvestorContactUpdateResponse,
  InvestorFundRelationship,
  InvestorFundRelationshipBulkCreateData,
  InvestorFundRelationshipBulkCreateResponse,
  InvestorFundRelationshipUpdateData,
  InvestorFundRelationshipUpdateResponse,
  InvestorStatistics,
  FundInvestorStatistics,
  InvestorFilters,
  InvestorFundTransferRequest,
  InvestorFundTransferResponse,
  InvestorInvestmentOverview,
  RawInvestorInvestmentOverview,
  PaginatedInvestorSummary,
  InvestorSummary,
  FundProxyVisibility,
  InvestorFundSummary,
} from '../types/investor';
import { INVESTOR_TYPES, type InvestorType } from '../types/investor';

// Type for any investor with fund relationships (works with both Summary and Detail)
type InvestorWithFundRelations = {
  fund_relationships: (InvestorFundSummary | InvestorFundRelationship)[];
};

function toInvestorFilterParams(filters: InvestorFilters): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (filters.investor_type) {
    searchParams.append('investor_type', filters.investor_type);
  }
  if (filters.kyc_status) {
    searchParams.append('kyc_status', filters.kyc_status);
  }
  if (filters.search) {
    searchParams.append('search', filters.search);
  }
  if (filters.has_pending_changes) {
    searchParams.append('has_pending_changes', 'true');
  }
  if (filters.include_archived) {
    searchParams.append('include_archived', 'true');
  }
  if (filters.fund) {
    searchParams.append('fund_id', filters.fund);
  }
  return searchParams;
}

class InvestorService {
  private baseUrl = '/investors';

  // Investor CRUD operations
  async getInvestors(
    params: {
      page?: number;
      page_size?: number;
      filters?: InvestorFilters;
    } = {}
  ): Promise<PaginatedInvestorSummary> {
    const { page = 1, page_size = 50, filters = {} } = params;

    const searchParams = toInvestorFilterParams(filters);
    searchParams.set('page', page.toString());
    searchParams.set('page_size', page_size.toString());

    const raw = await apiClient.get<PaginatedInvestorSummary>(
      `${this.baseUrl}?${searchParams.toString()}`
    );
    return mapPaginatedInvestorSummary(raw);
  }

  async downloadInvestorContactsExport(
    filters: InvestorFilters
  ): Promise<void> {
    const blob = await apiClient.getBlob(
      `${this.baseUrl}/export?${toInvestorFilterParams(filters).toString()}`
    );
    downloadBlobAsFile(
      blob,
      `investor-contacts-${new Date().toISOString().slice(0, 10)}.csv`
    );
  }

  async getInvestor(
    investorId: string,
    options: {
      include_contacts?: boolean;
      include_fund_relationships?: boolean;
    } = {}
  ): Promise<InvestorDetail> {
    const { include_contacts = true, include_fund_relationships = true } =
      options;

    const searchParams = new URLSearchParams({
      include_contacts: include_contacts.toString(),
      include_fund_relationships: include_fund_relationships.toString(),
    });

    const raw = await apiClient.get<InvestorDetail>(
      `${this.baseUrl}/${investorId}?${searchParams.toString()}`
    );
    return mapInvestorDetail(raw);
  }

  async createInvestor(data: InvestorCreateData): Promise<InvestorDetail> {
    return apiClient.post(this.baseUrl, data);
  }

  async updateInvestor(
    investorId: string,
    data: InvestorUpdateData
  ): Promise<InvestorUpdateResponse> {
    return apiClient.put(`${this.baseUrl}/${investorId}`, data);
  }

  async deleteInvestor(investorId: string): Promise<InvestorDeleteResponse> {
    return apiClient.delete(`${this.baseUrl}/${investorId}`);
  }

  async unarchiveInvestor(investorId: string): Promise<void> {
    return apiClient.post(`${this.baseUrl}/${investorId}/unarchive`);
  }

  // Email log (business emails sent to the investor, with delivery status).
  // Note: these live under /admin/investors, not this.baseUrl (/investors).
  async getInvestorEmails(
    investorId: string,
    params: InvestorEmailLogParams = {}
  ): Promise<InvestorEmailLogList> {
    return apiClient.get(`/admin/investors/${investorId}/emails`, { params });
  }

  async getInvestorEmail(
    investorId: string,
    emailId: string
  ): Promise<InvestorEmailLogDetail> {
    return apiClient.get(`/admin/investors/${investorId}/emails/${emailId}`);
  }

  async getExportLogs(
    params: InvestorExportLogParams
  ): Promise<InvestorExportLogList> {
    return apiClient.get('/admin/investor-exports', { params });
  }

  // Contact management
  async getInvestorContact(
    investorId: string,
    contactId: string
  ): Promise<InvestorContact> {
    return apiClient.get(`${this.baseUrl}/${investorId}/contacts/${contactId}`);
  }

  async createInvestorContact(
    investorId: string,
    data: InvestorContactCreateData
  ): Promise<InvestorContact> {
    return apiClient.post(`${this.baseUrl}/${investorId}/contacts`, data);
  }

  async updateInvestorContact(
    contactId: string,
    data: InvestorContactUpdateData
  ): Promise<InvestorContactUpdateResponse> {
    return apiClient.put(`${this.baseUrl}/contacts/${contactId}`, data);
  }

  async deleteInvestorContact(contactId: string): Promise<void> {
    return apiClient.delete(`${this.baseUrl}/contacts/${contactId}`);
  }

  async searchContacts(params: {
    q?: string;
    limit?: number;
  }): Promise<InvestorContactSearchResult[]> {
    return apiClient.get(`${this.baseUrl}/contacts/search`, { params });
  }

  // Fund relationship management
  async createFundRelationship(
    data: InvestorFundRelationshipBulkCreateData
  ): Promise<InvestorFundRelationshipBulkCreateResponse> {
    return apiClient.post(`${this.baseUrl}/fund-relationships`, data);
  }

  async updateFundRelationship(
    relationshipId: string,
    data: InvestorFundRelationshipUpdateData
  ): Promise<InvestorFundRelationshipUpdateResponse> {
    return apiClient.put(
      `${this.baseUrl}/fund-relationships/${relationshipId}`,
      data
    );
  }

  async deleteFundRelationship(
    relationshipId: string
  ): Promise<FundRelationshipDeleteResponse> {
    return apiClient.delete(
      `${this.baseUrl}/fund-relationships/${relationshipId}`
    );
  }

  async transferInvestorBetweenInvestors(
    data: InvestorFundTransferRequest
  ): Promise<InvestorFundTransferResponse> {
    const raw = await apiClient.post<InvestorFundTransferResponse>(
      `${this.baseUrl}/transfers`,
      data
    );
    return mapInvestorFundTransferResponse(
      raw as unknown as Parameters<typeof mapInvestorFundTransferResponse>[0]
    );
  }

  // Fund-specific queries
  async getInvestorsAvailableForFund(
    fundId: string,
    fundProxies: FundProxyVisibility = 'exclude'
  ): Promise<InvestorSummary[]> {
    return apiClient.get<InvestorSummary[]>(
      `${this.baseUrl}/funds/${fundId}/available-investors?fund_proxies=${fundProxies}`
    );
  }

  async getFundInvestors(
    fundId: string,
    status?: string
  ): Promise<InvestorDetail[]> {
    const raw = await apiClient.get<InvestorDetail[]>(
      `${this.baseUrl}/funds/${fundId}/investors`,
      { params: { status } }
    );
    return raw.map(mapInvestorDetail);
  }

  async getFundInvestorStatistics(
    fundId: string
  ): Promise<FundInvestorStatistics> {
    const raw = await apiClient.get<FundInvestorStatistics>(
      `${this.baseUrl}/funds/${fundId}/statistics`
    );
    return mapFundInvestorStatistics(
      raw as unknown as Parameters<typeof mapFundInvestorStatistics>[0]
    );
  }

  async getAllFundsCommitmentTotals(): Promise<Record<string, number>> {
    const raw = await apiClient.get<Record<string, string>>(
      `${this.baseUrl}/funds/commitment-totals`
    );
    return toNumberRecord(raw as Record<string, unknown>);
  }

  // Statistics
  async getInvestorStatistics(): Promise<InvestorStatistics> {
    return apiClient.get(`${this.baseUrl}/statistics`);
  }

  // Investment Overview
  async getInvestorOverview(
    investorId: string
  ): Promise<InvestorInvestmentOverview> {
    const raw = await apiClient.get<RawInvestorInvestmentOverview>(
      `${this.baseUrl}/${investorId}/overview`
    );
    return mapInvestorInvestmentOverview(raw);
  }

  getInvestorTypeLabel(type: string): string {
    return INVESTOR_TYPES[type as InvestorType] ?? type;
  }

  getKycStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Pending',
      verified: 'Verified',
      rejected: 'Rejected',
    };
    return labels[status] || status;
  }

  getKycStatusColor(status: string): string {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  getRelationshipStatusColor(status: string): string {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      exited: 'bg-gray-100 text-gray-800',
      defaulted: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  validateInvestorData(
    data: InvestorCreateData | InvestorUpdateData
  ): string[] {
    const errors: string[] = [];

    if ('name' in data && (!data.name || data.name.trim().length === 0)) {
      errors.push('Name is required');
    }

    if ('investor_type' in data && !data.investor_type) {
      errors.push('Investor type is required');
    }

    if ('iban' in data && data.iban && !this.isValidIban(data.iban)) {
      errors.push('Invalid IBAN');
    }

    if (
      'bank_swift_code' in data &&
      data.bank_swift_code &&
      !this.isValidSwiftCode(data.bank_swift_code)
    ) {
      errors.push('Invalid SWIFT/BIC code');
    }

    return errors;
  }

  private isValidIban(iban: string): boolean {
    // Basic IBAN validation (length and format)
    const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}[A-Z0-9]{0,16}$/;
    return ibanRegex.test(iban.replace(/\s+/g, '').toUpperCase());
  }

  private isValidSwiftCode(swift: string): boolean {
    // SWIFT/BIC code validation (8 or 11 characters)
    const swiftRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
    return swiftRegex.test(swift.toUpperCase());
  }

  // Fund relationship utilities
  getFundNames(investor: InvestorWithFundRelations): string {
    if (
      !investor.fund_relationships ||
      investor.fund_relationships.length === 0
    ) {
      return '';
    }
    return investor.fund_relationships
      .filter(
        (rel): rel is InvestorFundSummary =>
          'fund_name' in rel && !!rel.fund_name
      )
      .map((rel) => rel.fund_name)
      .join(', ');
  }

  getTotalCommitment(
    investor: InvestorWithFundRelations
  ): { amount: number; currency: string; otherCurrencyCount: number } | null {
    if (
      !investor.fund_relationships ||
      investor.fund_relationships.length === 0
    ) {
      return null;
    }

    const commitmentsByCurrency = investor.fund_relationships.reduce(
      (acc, rel) => {
        const currency = rel.commitment_currency;
        const amount = rel.commitment_amount;

        if (!amount || Number.isNaN(amount)) {
          return acc;
        }

        acc[currency] = (acc[currency] || 0) + amount;
        return acc;
      },
      {} as Record<string, number>
    );

    const currencies = Object.keys(commitmentsByCurrency);
    if (currencies.length === 0) {
      return null;
    }

    // Multiple currencies are reported as the first one plus a count; summing
    // across currencies would need FX rates we don't have here.
    return {
      amount: commitmentsByCurrency[currencies[0]],
      currency: currencies[0],
      otherCurrencyCount: currencies.length - 1,
    };
  }

  formatTotalCommitment(investor: InvestorWithFundRelations): string {
    const total = this.getTotalCommitment(investor);
    if (!total) {
      return '';
    }
    const formatted = formatCurrency(total.amount, {
      currency: total.currency,
    });
    return total.otherCurrencyCount > 0
      ? `${formatted} +${total.otherCurrencyCount}`
      : formatted;
  }
}

export const investorService = new InvestorService();
